import { makeNoise2D } from "./heightmap";
import type { FluidBlock, VoxelWorld } from "../types";
import {
    GRID_SIZE,
    RIVER_SEED_OFFSET,
    RIVER_NOISE_Z_FREQ,
    RIVER_CENTER_X_AMPLITUDE,
    RIVER_HALF_THICKNESS,
    RIVER_DEPTH,
    BASE_GROUND_OFFSET,
    BEDROCK_Y,
} from "../config";

export function generateRiverMask(
    seed: number,
    size = GRID_SIZE,
    halfThickness: number = RIVER_HALF_THICKNESS,
) {
    const noise2D = makeNoise2D(seed + RIVER_SEED_OFFSET);
    const mask = Array.from({ length: size }, () => Array(size).fill(false));

    for (let z = 0; z < size; z++) {
        const n = noise2D(0, z * RIVER_NOISE_Z_FREQ);
        const centerX = Math.floor(size / 2 + n * RIVER_CENTER_X_AMPLITUDE);
        for (let dx = -halfThickness; dx <= halfThickness; dx++) {
            const x = centerX + dx;
            if (x >= 0 && x < size) mask[x][z] = true;
        }
    }
    return mask;
}

/**
 * Add scattered pond cells via low-frequency noise. Used for swamp wetlands.
 */
export function addPonds(
    mask: boolean[][],
    seed: number,
    size = GRID_SIZE,
    threshold: number = 0.4,
) {
    const noise = makeNoise2D(seed + RIVER_SEED_OFFSET + 5000);
    const noise2 = makeNoise2D(seed + RIVER_SEED_OFFSET + 7777);
    for (let x = 0; x < size; x++) {
        for (let z = 0; z < size; z++) {
            // combine two octaves for blob-shaped ponds
            const n = noise(x * 0.08, z * 0.08) * 0.7 + noise2(x * 0.16, z * 0.16) * 0.3;
            if (n > threshold) mask[x][z] = true;
        }
    }
}

export function carveRiver(
    heights: number[][],
    riverMask: boolean[][],
    depth: number = RIVER_DEPTH
) {
    const N = heights.length;
    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            if (riverMask[x][z]) heights[x][z] -= depth;
        }
    }
    return heights;
}

export function carveRiverWithBanks(
    heights: number[][],
    riverMask: boolean[][],
    mainDepth: number = RIVER_DEPTH,
    bankDepth: number = Math.max(1, Math.floor(RIVER_DEPTH / 2))
) {
    const N = heights.length;
    const within = (x: number, z: number) => x >= 0 && x < N && z >= 0 && z < N;
    const isRiverCell = (x: number, z: number) => within(x, z) && riverMask[x][z];

    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            if (isRiverCell(x, z)) {
                heights[x][z] -= mainDepth;
            } else {
                // check 4-neighborhood for banks
                if (
                    isRiverCell(x + 1, z) ||
                    isRiverCell(x - 1, z) ||
                    isRiverCell(x, z + 1) ||
                    isRiverCell(x, z - 1)
                ) {
                    heights[x][z] -= bankDepth;
                }
            }
        }
    }
    return heights;
}

export function carveWaterfalls(
    world: VoxelWorld,
    heights: number[][],
    riverMask: boolean[][],
    fluid: FluidBlock,
): void {
    const N = heights.length;
    for (const z of [0, N - 1]) {
        for (let x = 0; x < N; x++) {
            if (!riverMask[x][z]) continue;
            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            for (let y = BEDROCK_Y + 1; y <= topAbs; y++) {
                world.set(x, y, z, fluid);
            }
        }
    }
}
